class RCContextualPriceSort extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.controller = new AbortController();
    queueMicrotask(() => this.sortAllPages());
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  metadata(element = this) {
    const data = element.dataset;
    return {
      section: data.section,
      count: Number(data.count),
      pages: Number(data.pages),
      page: Number(data.page),
      size: Number(data.pageSize),
      pageParam: data.pageParam || 'page',
      sort: data.sort,
      currency: data.currency,
      country: data.country,
      locale: data.locale
    };
  }

  validateMetadata(actual, expected, requestedPage) {
    for (const name of ['section', 'count', 'pages', 'size', 'pageParam', 'sort', 'currency', 'country', 'locale']) {
      if (actual[name] !== expected[name]) throw new Error(`Inconsistent result context: ${name}`);
    }
    if (actual.page !== requestedPage) throw new Error('Unexpected result page');
  }

  readProducts(grid, metadata) {
    const nodes = [...grid.querySelectorAll(':scope > [data-product-id]')];
    const expectedCount = Math.min(metadata.size, metadata.count - (metadata.page - 1) * metadata.size);
    if (nodes.length !== expectedCount) throw new Error('Incomplete result page');
    return nodes.map((node) => {
      const rawPrice = node.getAttribute('data-rc-sort-price');
      const price = Number(rawPrice);
      if (rawPrice === null || rawPrice === '' || !Number.isSafeInteger(price) || price < 0) throw new Error('Missing contextual price');
      // 30-sep-2026 · Los agotados van al final también al ordenar por precio.
      return { node, id: node.dataset.productId, price, soldOut: node.getAttribute('data-rc-available') === 'false' ? 1 : 0 };
    });
  }

  async sortAllPages() {
    const metadata = this.metadata();
    if (!['price-ascending', 'price-descending'].includes(metadata.sort)) return;
    const root = this.closest('results-list');
    const grid = root?.querySelector('ul.product-grid');
    if (!grid) return;
    const previousInert = grid.inert;
    grid.inert = true;
    grid.setAttribute('aria-busy', 'true');
    this.dataset.state = 'loading';
    const status = this.querySelector('[data-rc-sort-status]');
    if (status) status.textContent = this.dataset.loading;
    try {
      if (!Number.isSafeInteger(metadata.count) || metadata.count < 1 || !Number.isSafeInteger(metadata.size) || metadata.size < 1 || metadata.pages !== Math.ceil(metadata.count / metadata.size) || metadata.page < 1 || metadata.page > metadata.pages) throw new Error('Invalid pagination');
      if (metadata.pages > 100) throw new Error('Result set exceeds the supported page budget');
      const baseURL = new URL(window.location.href);
      if (/\/search\/?$/.test(baseURL.pathname) && baseURL.searchParams.get('type') !== 'product') {
        baseURL.searchParams.set('type', 'product');
        window.location.replace(baseURL);
        return;
      }
      const products = this.readProducts(grid, metadata);
      for (let pageNumber = 1; pageNumber <= metadata.pages; pageNumber += 1) {
        if (pageNumber === metadata.page) continue;
        const url = new URL(baseURL);
        url.searchParams.set(metadata.pageParam, String(pageNumber));
        url.searchParams.set('section_id', metadata.section);
        const response = await fetch(url, { signal: this.controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Result page returned ${response.status}`);
        const documentForPage = new DOMParser().parseFromString(await response.text(), 'text/html');
        const marker = [...documentForPage.querySelectorAll('rc-contextual-price-sort')].find((element) => element.dataset.section === metadata.section);
        if (!marker) throw new Error('Missing result context');
        const pageMetadata = this.metadata(marker);
        this.validateMetadata(pageMetadata, metadata, pageNumber);
        const pageGrid = marker.closest('results-list')?.querySelector('ul.product-grid');
        if (!pageGrid) throw new Error('Missing result grid');
        products.push(...this.readProducts(pageGrid, pageMetadata));
      }
      if (this.controller.signal.aborted || !this.isConnected) return;
      if (products.length !== metadata.count || new Set(products.map((product) => product.id)).size !== metadata.count) throw new Error('Incomplete or duplicated result set');
      const direction = metadata.sort === 'price-descending' ? -1 : 1;
      products.sort((left, right) => (left.soldOut - right.soldOut) || direction * (left.price - right.price) || left.id.localeCompare(right.id, 'en', { numeric: true }));
      const offset = (metadata.page - 1) * metadata.size;
      const selected = products.slice(offset, offset + metadata.size);
      const slots = [...grid.children];
      let productIndex = 0;
      const fragment = document.createDocumentFragment();
      for (const slot of slots) {
        if (!slot.hasAttribute('data-product-id')) {
          fragment.append(slot);
          continue;
        }
        const replacement = selected[productIndex++].node.cloneNode(true);
        replacement.dataset.page = String(metadata.page);
        fragment.append(replacement);
      }
      grid.replaceChildren(fragment);
      this.dataset.state = 'complete';
      if (status) status.textContent = '';
      this.hidden = true;
      root.dispatchEvent(new CustomEvent('rc:price-sort:complete', { bubbles: true, detail: { count: products.length, currency: metadata.currency, page: metadata.page } }));
    } catch (error) {
      if (error.name === 'AbortError' || this.controller.signal.aborted) return;
      this.dataset.state = 'error';
      this.hidden = false;
      if (status) status.textContent = this.dataset.error;
      const retry = this.querySelector('[data-rc-sort-retry]');
      if (retry) {
        retry.hidden = false;
        retry.addEventListener('click', () => window.location.reload(), { once: true });
      }
    } finally {
      grid.inert = previousInert;
      grid.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('rc-contextual-price-sort')) customElements.define('rc-contextual-price-sort', RCContextualPriceSort);
